import React, { useState, useEffect, useRef, useCallback } from 'react';
import healthCheckService from '../../services/healthCheck';

export interface LoginConnectionStatusProps {
  className?: string;
  variant?: 'light' | 'dark';
  onStatusChange?: (connected: boolean) => void;
}

export interface ConnectionState {
  connected: boolean;
  lastUpdate: Date;
}

export const LoginConnectionStatus: React.FC<LoginConnectionStatusProps> = ({
  className = '',
  variant = 'light',
  onStatusChange,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const isCheckingRef = useRef(false);
  const onStatusChangeRef = useRef(onStatusChange);
  
  // Keep the callback ref updated
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const checkBackendHealth = useCallback(async () => {
    // Prevent concurrent health checks and skip if not mounted
    if (isCheckingRef.current || !isMountedRef.current) {
      return;
    }
    
    isCheckingRef.current = true;
    
    try {
      const result = await healthCheckService.checkHealth(true);
      const connected = result.success;
      
      // Double-check mounted state after async operation
      if (isMountedRef.current) {
        setIsConnected(connected);
        onStatusChangeRef.current?.(connected);
      }
    } catch (error) {
      console.error('LoginConnectionStatus: Health check failed:', error);
      if (isMountedRef.current) {
        setIsConnected(false);
        onStatusChangeRef.current?.(false);
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, []); // Remove onStatusChange dependency to prevent recreation

  useEffect(() => {
    isMountedRef.current = true;
    
    // Add a small delay to avoid React StrictMode double execution issues
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        checkBackendHealth();
      }
    }, 100);
    
    // Set up periodic health checks every 10 seconds
    const intervalId = setInterval(() => {
      if (isMountedRef.current && !isCheckingRef.current) {
        checkBackendHealth();
      }
    }, 10000);
    
    // Store interval reference
    intervalRef.current = intervalId;

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Remove all dependencies to run only once per mount

  const isDark = variant === 'dark';

  const themeClasses = isDark
    ? isConnected
      ? 'bg-emerald-400/10 text-emerald-200 border-emerald-400/25 backdrop-blur-sm'
      : 'bg-red-400/10 text-red-200 border-red-400/25 backdrop-blur-sm'
    : isConnected
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-red-50 text-red-700 border-red-200';

  const dotColor = isConnected ? 'bg-emerald-400' : 'bg-red-400';

  return (
    <div className={`flex justify-center ${className}`}>
      <div
        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 border ${themeClasses}`}
        role="status"
        aria-live="polite"
        aria-label={`Backend connection status: ${isConnected ? 'Connected' : 'Disconnected'}`}
        data-testid="login-connection-status"
        data-connected={isConnected}
      >
        <span className="relative inline-flex h-2 w-2">
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${dotColor} ${
              isConnected ? 'animate-ping' : ''
            }`}
          ></span>
          <span className={`relative inline-flex h-2 w-2 rounded-full ${dotColor}`}></span>
        </span>
        <span>{isConnected ? 'System Online' : 'System Offline'}</span>
      </div>
    </div>
  );
};

export default LoginConnectionStatus;